import {
  Box,
  Button,
  Loader,
  Pagination,
  PaginationWrapper,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { AddCircleOutlineIcon } from "@braintrust/braintrust-ui-components/Icons";
import { EmployerDashboardLayout } from "@js/apps/employer/views/dashboard/layout";
import { openRosterFormModal } from "@js/apps/rosters/components/roster-form-modal";
import { RostersFilters } from "@js/apps/rosters/components/rosters-filters";
import { RostersList } from "@js/apps/rosters/components/rosters-list";
import { useRostersFilters } from "@js/apps/rosters/hooks/use-rosters-filters";
import { RouterLink } from "@js/components/link";
import { useNavigate } from "@js/hooks";

import { useGetRostersQuery } from "../../api";
import type { RosterVisibilityType } from "../../types";

export const RostersListPage = () => {
  const navigate = useNavigate();
  const { filters } = useRostersFilters();

  const { data, isLoading } = useGetRostersQuery({
    search: filters.search ?? undefined,
    visibility: (filters.visibility ?? undefined) as
      RosterVisibilityType | undefined,
    page: filters.page,
  });

  const handleCreateClick = () => {
    openRosterFormModal({
      onSaved: (roster) => navigate(`/rosters/${roster.id}/`),
    });
  };

  return (
    <EmployerDashboardLayout
      pageTitle="Rosters"
      bgcolor="var(--soft-violet)"
      setBgOnBody
    >
      <Stack sx={{ gap: 3 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Stack sx={{ gap: 0.75, maxWidth: "40rem" }}>
            <Typography component="h1" variant="title" size="medium">
              Rosters
            </Typography>
            <Typography
              component="p"
              variant="paragraph"
              sx={{ color: "var(--grey-2)" }}
            >
              Curated lists of freelancers you're keeping an eye on. Group
              candidates by role or project, and leave notes on why they stood
              out.
            </Typography>
          </Stack>
          <Button
            variant="positive"
            shape="squared"
            size="medium"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleCreateClick}
          >
            Create roster
          </Button>
        </Stack>
        <RostersFilters />
        {isLoading ? (
          <Box sx={{ position: "relative", minHeight: "10rem" }}>
            <Loader centered />
          </Box>
        ) : (
          <>
            <RostersList rosters={data?.results ?? []} isLoading={isLoading} />
            <PaginationWrapper>
              <Pagination
                page={filters.page}
                count={data?.count ?? 0}
                perPage={25}
                RouterLink={RouterLink}
              />
            </PaginationWrapper>
          </>
        )}
      </Stack>
    </EmployerDashboardLayout>
  );
};
